/**
 * ObjectDetailsDialog - Dialogue pour afficher les détails d'un objet et permettre de le ramasser/déposer
 * Identique à Flutter ObjectDetailsDialog
 */

import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  ScrollView,
  TouchableOpacity,
  SafeAreaView,
  Platform,
  Image,
  Alert,
} from 'react-native';
import {FontAwesomeIcon} from '@fortawesome/react-native-fontawesome';
import {faTimes, faShoppingBag, faSearch, faPlus, faMinus} from '@fortawesome/free-solid-svg-icons';
import {usePlayer} from '../contexts/PlayerContext';
import {SolidObject} from '../models/SolidObject';
import {Fonts} from '../config/fonts';

interface ObjectDetailsDialogProps {
  visible: boolean;
  object: SolidObject | null;
  isPickable: boolean; // Si l'objet est dans step.objects (ramassable)
  isInInventory: boolean; // Si l'objet est dans l'inventaire
  onClose: () => void;
  onExamine?: (object: SolidObject) => void; // Callback pour ouvrir ExamineObjectDialog
}

const ObjectDetailsDialog: React.FC<ObjectDetailsDialogProps> = ({
  visible,
  object,
  isPickable,
  isInInventory,
  onClose,
  onExamine,
}) => {
  const {gameState, currentStep, executeAction} = usePlayer();

  if (!object) {
    return null;
  }

  const getImageUri = (imageData?: string): string | null => {
    if (!imageData) return null;
    if (imageData.startsWith('data:image')) {
      return imageData;
    }
    return `data:image/png;base64,${imageData}`;
  };

  const imageUri = getImageUri(object.image);

  const handlePickUp = async () => {
    try {
      await executeAction('pick_up', {id: object.id});
      Alert.alert('Succès', `${object.name} ramassé !`);
      onClose();
    } catch (error: any) {
      console.error('[ObjectDetailsDialog] Error picking up object:', error);
      Alert.alert('Erreur', `Impossible de ramasser ${object.name}: ${error.message}`);
    }
  };

  const handleDrop = async () => {
    try {
      await executeAction('drop', {id: object.id});
      Alert.alert('Succès', `${object.name} déposé !`);
      onClose();
    } catch (error: any) {
      console.error('[ObjectDetailsDialog] Error dropping object:', error);
      Alert.alert('Erreur', `Impossible de déposer ${object.name}: ${error.message}`);
    }
  };

  const handleExamine = () => {
    if (onExamine) {
      onExamine(object);
    }
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.modalOverlay}>
        <SafeAreaView style={styles.modalContainer}>
          <View style={styles.modalContent}>
            {/* Image de l'objet */}
            {imageUri && (
              <View style={styles.imageContainer}>
                <Image source={{uri: imageUri}} style={styles.image} resizeMode="cover" />
              </View>
            )}

            {/* Header */}
            <View style={styles.header}>
              <Text style={styles.title}>{object.name}</Text>
              <TouchableOpacity style={styles.closeButton} onPress={onClose}>
                <FontAwesomeIcon icon={faTimes} size={24} color="#333" />
              </TouchableOpacity>
            </View>

            {/* Description */}
            {object.description && (
              <ScrollView style={styles.scrollView}>
                <Text style={styles.description}>{object.description}</Text>
              </ScrollView>
            )}

            {/* Actions */}
            <View style={styles.actionsContainer}>
              {/* Bouton Examiner */}
              {imageUri && (
                <TouchableOpacity
                  style={[styles.actionButton, styles.examineButton]}
                  onPress={handleExamine}
                  activeOpacity={0.85}>
                  <FontAwesomeIcon icon={faSearch} size={18} color="#fff" />
                  <Text style={styles.actionButtonText}>Examiner</Text>
                </TouchableOpacity>
              )}

              {/* Bouton Ramasser ou Déposer */}
              {isPickable && (
                <TouchableOpacity
                  style={[styles.actionButton, styles.pickUpButton]}
                  onPress={handlePickUp}
                  activeOpacity={0.85}>
                  <FontAwesomeIcon icon={faPlus} size={18} color="#fff" />
                  <Text style={styles.actionButtonText}>Prendre</Text>
                </TouchableOpacity>
              )}

              {isInInventory && (
                <TouchableOpacity
                  style={[styles.actionButton, styles.dropButton]}
                  onPress={handleDrop}
                  activeOpacity={0.85}>
                  <FontAwesomeIcon icon={faMinus} size={18} color="#fff" />
                  <Text style={styles.actionButtonText}>Lâcher</Text>
                </TouchableOpacity>
              )}
            </View>
          </View>
        </SafeAreaView>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContainer: {
    width: '90%',
    maxWidth: 500,
    maxHeight: '80%',
  },
  modalContent: {
    backgroundColor: '#F5F3ED',
    ...Platform.select({
      web: {
        borderTopLeftRadius: 16,
        borderTopRightRadius: 12,
        borderBottomRightRadius: 16,
        borderBottomLeftRadius: 12,
      },
      default: {
        borderRadius: 16,
      },
    }),
    overflow: 'hidden',
  },
  imageContainer: {
    width: '100%',
    height: 200,
    backgroundColor: '#E8D5C4',
  },
  image: {
    width: '100%',
    height: '100%',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#D4C4B0',
  },
  title: {
    flex: 1,
    fontSize: 24,
    fontWeight: '600',
    color: '#2C2C2C',
    fontFamily: Fonts.title,
  },
  closeButton: {
    padding: 8,
  },
  scrollView: {
    maxHeight: 200,
  },
  description: {
    fontSize: 16,
    lineHeight: 24,
    color: '#2C2C2C',
    fontFamily: Fonts.reading,
    padding: 20,
  },
  actionsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-evenly',
    padding: 16,
    gap: 12,
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    gap: 8,
  },
  examineButton: {
    backgroundColor: '#4A90E2',
  },
  pickUpButton: {
    backgroundColor: '#4CAF50',
  },
  dropButton: {
    backgroundColor: '#FF9800',
  },
  actionButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '500',
    fontFamily: Fonts.ui,
  },
});

export default ObjectDetailsDialog;
